import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function HiddenKitsilanoCommerce() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "hidden-kitsilano-commerce";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Hidden Restaurants & Cafés",
    "section-2": "Hidden Shops & Boutiques",
    "section-3": "Hidden Nightlife & Culture",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/your-government/noise-control.aspx",
      label: "City of Vancouver – Noise Control By-law",
    },
    {
      url: "https://www.destinationvancouver.com/vancouver/neighbourhoods/kitsilano/",
      label: "Destination Vancouver – Kitsilano Guide",
    },
    {
      url: "https://kitsonbroadway.com/",
      label: "Kits on Broadway – Kitsilano Business Association",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" should match the section so the scroll link works properly.
  // The coordinates used below match those mentioned in the text sections.
  const locations = [
    {
      id: "hidden-restaurants-and-cafes", // We'll map them to the relevant paragraphs below
      lat: 49.26650,
      lng: -123.15306,
      popupText: "Arbutus Coffee",
    },
    {
      id: "hidden-restaurants-and-cafes-2",
      lat: 49.26691,
      lng: -123.14053,
      popupText: "Beaucoup Bakery",
    },
    {
      id: "hidden-restaurants-and-cafes-3",
      lat: 49.26792,
      lng: -123.14967,
      popupText: "Zulu Records (nearby hidden gem on W 4th)",
    },
    {
      id: "hidden-shops-and-boutiques",
      lat: 49.26465,
      lng: -123.17186,
      popupText: "Stepback",
    },
    {
      id: "hidden-shops-and-boutiques-2",
      lat: 49.26440,
      lng: -123.16335,
      popupText: "Persia Foods",
    },
    {
      id: "hidden-nightlife-and-culture",
      lat: 49.26400,
      lng: -123.17200,
      popupText: "The Fringe Café",
    },
    {
      id: "hidden-nightlife-and-culture-2",
      lat: 49.26860,
      lng: -123.18500,
      popupText: "Jericho Arts Centre",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Hidden Kitsilano Commerce: Exploring Vancouver’s Best-Kept Secrets</title>
        <meta
          name="description"
          content="Discover the hidden businesses of Kitsilano, Vancouver, including secret cafes, shops, and nightlife spots that locals love."
        />
        <meta
          name="keywords"
          content="Kitsilano, Vancouver, hidden commerce, secret shops, local cafes"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/hidden-kitsilano-commerce.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Hidden Kitsilano Commerce: Exploring Vancouver’s Best-Kept Secrets"
        />
        <meta
          property="og:description"
          content="A practical guide to Kitsilano’s hidden eateries, boutiques, and nightlife. Discover local favorites off the beaten path."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/hidden-kitsilano-commerce.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/hidden-kitsilano-commerce.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Hidden Kitsilano Commerce: Exploring Vancouver’s Best-Kept Secrets"
        />
        <meta
          name="twitter:description"
          content="Unearth hidden gems in Vancouver's Kitsilano neighborhood, from cozy cafes to vintage shops."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/hidden-kitsilano-commerce.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Hidden businesses in Kitsilano"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Kitsilano, a laid-back beachside neighborhood in Vancouver, is
              known for its trendy hotspots and popular restaurants. But beyond
              the well-known cafes, you'll find a world of lesser-known
              businesses that locals love. These hidden treasures—from vintage
              boutiques to unassuming eateries—offer an authentic Kitsilano
              experience. In this guide, we’ll uncover these spots so you can
              explore the heart of the neighborhood in a whole new way.
            </p>

            {/* SECTION: Hidden Restaurants & Cafés */}
            <h2 id="section-1">Hidden Restaurants & Cafés</h2>
            <p>
              Kitsilano’s dining scene extends far beyond mainstream brunch
              establishments. Tucked away along quiet streets are cozy cafes and
              award-winning restaurants that remain under the radar of most
              visitors.
            </p>

            {/* Example Location 1: Arbutus Coffee */}
            <p
              ref={(el) => setSpotRef("hidden-restaurants-and-cafes", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Arbutus Coffee</strong>{" "}
              <Marker
                coordY={49.26650}
                coordX={-123.15306}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Housed in a heritage 1907 building (2200 Arbutus St), Arbutus
              Coffee offers baked goods and a peaceful patio. Locals cherish the
              nostalgic vibe and family-run atmosphere. Expect to spend around
              $4–8 on coffee and pastries. It closes by 6:00pm, so drop by early
              for a cozy morning or afternoon break.
            </p>

            {/* Example Location 2: Beaucoup Bakery */}
            <p
              ref={(el) => setSpotRef("hidden-restaurants-and-cafes-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Beaucoup Bakery & Café</strong>{" "}
              <Marker
                coordY={49.26691}
                coordX={-123.14053}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Tucked away at 2150 Fir St, Beaucoup Bakery is a petite,
              award-winning French-inspired patisserie. Almond croissants, salted
              caramel éclairs, and artisanal lattes keep regulars coming back.
              Despite glowing reviews, the location remains low-key, adding to
              its hidden-gem charm.
            </p>

            <p>
              Whether you're craving a quiet morning coffee or a gourmet dinner
              at a tiny but renowned spot, Kitsilano’s hidden restaurants and
              cafes are ideal for exploring local flavors without the typical
              tourist queues.
            </p>

            {/* Example mention of a location near Zulu Records if relevant */}
            <p
              ref={(el) => setSpotRef("hidden-restaurants-and-cafes-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Zulu Records</strong>{" "}
              <Marker
                coordY={49.26792}
                coordX={-123.14967}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – While primarily a legendary music store at 1972 W 4th Ave, it's
              surrounded by small hidden eateries that often go unnoticed. Pop
              in for vinyls, then stroll a few doors down to find quaint lunch
              spots offering budget-friendly bites.
            </p>

            {/* SECTION: Hidden Shops & Boutiques */}
            <h2 id="section-2">Hidden Shops & Boutiques</h2>
            <p>
              Kitsilano’s hidden commerce also shines in its boutique scene.
              From vintage treasures to locally sourced groceries, these shops
              prove there's more to Kits than just chain stores.
            </p>

            {/* Example Location 3: Stepback */}
            <p
              ref={(el) => setSpotRef("hidden-shops-and-boutiques", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stepback</strong>{" "}
              <Marker
                coordY={49.26465}
                coordX={-123.17186}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located at 2936 W Broadway, Stepback feels like stepping into a
              retro time capsule. Curated vintage decor, typewriters, and other
              collectibles line the shelves. Prices range from $10 for mugs up
              to $100+ for larger pieces.
            </p>

            {/* Example Location 4: Persia Foods */}
            <p
              ref={(el) => setSpotRef("hidden-shops-and-boutiques-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Persia Foods</strong>{" "}
              <Marker
                coordY={49.26440}
                coordX={-123.16335}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A hidden hero among grocery stores. Tucked at 2827 W Broadway,
              Persia Foods boasts an international selection at affordable
              prices—fresh produce, Middle Eastern spices, and even rosewater
              ice cream. It's popular with local chefs and home cooks seeking
              authentic ingredients for less.
            </p>

            <p>
              These spots exemplify Kitsilano’s distinct blend of neighborhood
              nostalgia and global flavor. Wander just a few blocks off the main
              avenues, and you’re bound to stumble upon special finds.
            </p>

            {/* SECTION: Hidden Nightlife & Culture */}
            <h2 id="section-3">Hidden Nightlife & Culture</h2>
            <p>
              Though Kitsilano isn’t known for a wild nightlife, it does boast a
              handful of under-the-radar spots where locals unwind or enjoy a
              cultural fix after dark.
            </p>

            {/* Example Location 5: The Fringe Café */}
            <p
              ref={(el) => setSpotRef("hidden-nightlife-and-culture", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Fringe Café</strong>{" "}
              <Marker
                coordY={49.26400}
                coordX={-123.17200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – As Kitsilano’s oldest bar (3124 W Broadway), the Fringe boasts
              open-mic nights and a friendly community vibe. Drinks are
              affordable, and the eclectic decor adds to its quirky charm. A
              staple for residents who prefer laid-back evenings over loud
              clubs.
            </p>

            {/* Example Location 6: Jericho Arts Centre */}
            <p
              ref={(el) => setSpotRef("hidden-nightlife-and-culture-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Jericho Arts Centre</strong>{" "}
              <Marker
                coordY={49.26860}
                coordX={-123.18500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located at 1675 Discovery St near Jericho Beach, this intimate
              community-run theatre often hosts indie plays and local
              performances. Tickets range from $20–$30, making it an affordable
              cultural gem hidden in a scenic park setting.
            </p>

            <p>
              From open-mic nights to small-stage theatre, Kitsilano’s hidden
              nightlife and cultural scene proves that not all excitement
              happens downtown. Take the evening to discover a different side of
              Vancouver’s creative spirit.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Businesses in Vancouver must comply with city bylaws, including
              noise control rules (especially important for late-night events)
              and waste disposal guidelines. Many shops encourage you to bring
              reusable bags or cups, aligning with Kitsilano’s eco-conscious
              culture. Parking can be tight on residential streets, so pay
              attention to permit signs. 
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Can I explore Kitsilano’s hidden spots without a car?</h3>
              <p>
                Absolutely. Kitsilano is well-served by public transit (buses
                #4, #7, #99 B-Line) and is very walkable. Biking is also
                popular, with many streets featuring bike lanes.
              </p>

              <h3>2. Do I need reservations for these hidden restaurants?</h3>
              <p>
                For smaller or highly rated eateries (like Maenam, not listed
                above but nearby), a reservation can help, especially at peak
                dinner times. For casual cafes and stores, walk-ins are fine.
              </p>

              <h3>3. Are these locations open year-round?</h3>
              <p>
                Generally, yes. Most operate year-round, though their hours may
                shorten in winter or on holidays. Always check the latest
                operating schedule to avoid disappointment.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Kitsilano is more than its famed beaches and busy brunch spots. By
              venturing a block or two off the main strips, you’ll uncover a
              neighborhood filled with vintage finds, globally inspired foods,
              and friendly bars. Whether you’re a first-time visitor or a
              long-time Vancouverite, these lesser-known gems invite you to
              experience Kitsilano in a more personal, relaxed way. Don’t be
              afraid to wander and chat with shop owners—they often have even
              more tips on what to explore next!
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}